import { batch } from 'react-redux';
import { map } from 'lodash/fp';
import { ServiceType } from '@mews/enums';
import { EntityDeletionState } from '@mews-commander/data-access';
import { callApiInChunks, createFetchAction, notEmpty } from '@mews-commander/utils';
import { getCurrentEnterpriseId } from '@mews-commander/core';
import prefix from '../prefix';
import { setProductCategories } from '../ducks/productCategories';
import { dispatchScopedGroupedEntityActions } from '../ducks/utils';
import { setServices } from '../ducks/services';
import { setProducts } from '../ducks/products';
import { setVouchers } from '../ducks/vouchers';
import { setBookingEngines } from '../ducks/bookingEngine';
import { setBusinessSegments } from '../ducks/businessSegments';
import { setRegistrationCardConfigurations } from '../ducks/registrationCardConfigurations';
import { setAgeCategories } from '../ducks/ageCategories';
import { fetchAvailabilityBlocks } from './availabilityBlocks';

const SERVICES_ADD = prefix('SERVICES_ADD');
const SERVICES_FETCH = prefix('SERVICES_FETCH');
const SERVICE_AVALABILITIES_UPDATE = prefix('SERVICE_AVALABILITIES_UPDATE');

const fetch =
    (payload: $TSFixMe) =>
    (dispatch: $TSFixMe, getState: $TSFixMe, { container }: { container: () => CommanderContainer }) => {
        const promise = container()
            .serviceComponent.getAll(payload)
            .then(
                ({
                    products,
                    productCategories,
                    services,
                    vouchers,
                    businessSegments,
                    bookingEngines,
                    registrationCardConfigurations,
                    ageCategories,
                }: $TSFixMe) => {
                    batch(() => {
                        if (notEmpty(products)) {
                            dispatch(setProducts(products));
                        }
                        if (notEmpty(productCategories)) {
                            dispatch(
                                dispatchScopedGroupedEntityActions({
                                    entities: productCategories,
                                    action: setProductCategories,
                                    scopeName: 'serviceId',
                                })
                            );
                        }
                        if (notEmpty(services)) {
                            dispatch(setServices(services));
                        }
                        if (notEmpty(vouchers)) {
                            dispatch(setVouchers(vouchers));
                        }
                        if (notEmpty(businessSegments)) {
                            dispatch(setBusinessSegments(businessSegments));
                        }
                        if (notEmpty(bookingEngines)) {
                            dispatch(setBookingEngines(bookingEngines));
                        }
                        if (notEmpty(registrationCardConfigurations)) {
                            dispatch(setRegistrationCardConfigurations(registrationCardConfigurations));
                        }
                        if (notEmpty(ageCategories)) {
                            dispatch(setAgeCategories(ageCategories));
                        }
                    });

                    return { products, services };
                }
            );

        return dispatch(createFetchAction(SERVICES_FETCH, promise));
    };

interface FetchEnterpriseServicesProps {
    enterpriseIds: string[];
    extent?: Record<string, boolean>;
    serviceIds?: string[];
}

export const fetchAllEnterpriseServices = ({ enterpriseIds, serviceIds, extent = {} }: FetchEnterpriseServicesProps) =>
    fetch({ enterpriseIds, serviceIds, extent, serviceDeletionState: EntityDeletionState.Everything });

export const fetchActiveEnterpriseServices = ({
    enterpriseIds,
    serviceIds,
    extent = {},
}: FetchEnterpriseServicesProps) =>
    fetch({ enterpriseIds, serviceIds, extent, serviceDeletionState: EntityDeletionState.NonDeleted });

interface FetchBookableServicesProps {
    enterpriseId?: string;
    enterpriseIds: string[];
    extent?: Record<string, boolean>;
}

export const fetchAllBookableServices = ({ enterpriseId, enterpriseIds, extent = {} }: FetchBookableServicesProps) =>
    fetch({
        enterpriseId,
        enterpriseIds,
        extent,
        serviceType: ServiceType.Bookable,
        serviceDeletionState: EntityDeletionState.Everything,
    });

export const fetchActiveBookableServices = ({ enterpriseId, enterpriseIds, extent = {} }: FetchBookableServicesProps) =>
    fetch({
        enterpriseId,
        enterpriseIds,
        extent,
        serviceType: ServiceType.Bookable,
        serviceDeletionState: EntityDeletionState.NonDeleted,
    });

export const fetchServiceProductCategories = ({ serviceIds, enterpriseIds }: $TSFixMe) =>
    fetch({ serviceIds, enterpriseIds, extent: { productCategories: true } });

const updateAvailability =
    ({ serviceId, availabilityUpdates, availabilityBlockId }: $TSFixMe) =>
    (dispatch: $TSFixMe, getState: $TSFixMe, { container }: { container: () => CommanderContainer }) => {
        // @ts-expect-error $TSFixMeError: Argument of type '{ apiCall: any; processChunks: _.LodashMap1x1<unknown[], { serviceId: any; availabilityUpdates: unknow...
        const promise = callApiInChunks({
            apiCall: container().serviceComponent.updateAvailability,
            processChunks: map((updates) => ({ serviceId, availabilityUpdates: updates })),
            serial: true,
        })(availabilityUpdates);

        return dispatch(createFetchAction(SERVICE_AVALABILITIES_UPDATE, promise)).then(() => {
            const enterpriseId = getCurrentEnterpriseId();
            return dispatch(
                fetchAvailabilityBlocks({
                    availabilityBlockIds: [availabilityBlockId],
                    enterpriseIds: [enterpriseId],
                    extent: {
                        adjustments: true,
                        profiles: false,
                        serviceOrders: false,
                    },
                })
            );
        });
    };

export const updateServiceAvailability = ({ serviceId, availabilityUpdates, availabilityBlockId }: $TSFixMe) =>
    updateAvailability({ serviceId, availabilityUpdates, availabilityBlockId });

export const addServices =
    (params: $TSFixMe) =>
    (dispatch: $TSFixMe, getState: $TSFixMe, { container }: { container: () => CommanderContainer }) => {
        const promise = container()
            .serviceComponent.add(params)
            .then(
                ({
                    products,
                    productCategories,
                    services,
                    vouchers,
                    businessSegments,
                    bookingEngines,
                    registrationCardConfigurations,
                    ageCategories,
                }: $TSFixMe) => {
                    batch(() => {
                        if (notEmpty(products)) {
                            dispatch(setProducts(products));
                        }
                        if (notEmpty(productCategories)) {
                            dispatch(
                                dispatchScopedGroupedEntityActions({
                                    entities: productCategories,
                                    action: setProductCategories,
                                    scopeName: 'serviceId',
                                })
                            );
                        }
                        if (notEmpty(services)) {
                            dispatch(setServices(services));
                        }
                        if (notEmpty(vouchers)) {
                            dispatch(setVouchers(vouchers));
                        }
                        if (notEmpty(businessSegments)) {
                            dispatch(setBusinessSegments(businessSegments));
                        }
                        if (notEmpty(bookingEngines)) {
                            dispatch(setBookingEngines(bookingEngines));
                        }
                        if (notEmpty(registrationCardConfigurations)) {
                            dispatch(setRegistrationCardConfigurations(registrationCardConfigurations));
                        }
                        if (notEmpty(ageCategories)) {
                            dispatch(setAgeCategories(ageCategories));
                        }
                    });

                    return services;
                }
            );
        return dispatch(createFetchAction(SERVICES_ADD, promise));
    };
